import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Radio,
  Row,
  Select,
  Space,
  Modal,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { valveService } from "../../services/valve.service";
import UploadImage from "../../components/UploadImage";

function ValvesDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [valveData, setValveData] = useState({});
  const [previousStatus, setPreviousStatus] = useState(null);
  const [isStatusProcessing, setIsStatusProcessing] = useState(false);
  const [location_pic, setLocationPic] = useState(null);
  const [gis_pic, setGisPic] = useState(null);
  const [jg_pic_near, setJgPicNear] = useState(null);
  const [jg_pic_far, setJgPicFar] = useState(null);
  // Add a state to track uploading status for all image components
  const [uploadingImages, setUploadingImages] = useState({
    location_pic: false,
    gis_pic: false,
    jg_pic_near: false,
    jg_pic_far: false,
  });

  // Helper function to check if any image is currently uploading
  const isAnyImageUploading = () => {
    return Object.values(uploadingImages).some((status) => status === true);
  };

  const districtOptions = [
    { value: "钟楼区", label: "钟楼区" },
    { value: "天宁区", label: "天宁区" },
    { value: "经开区", label: "经开区" },
    { value: "新北区", label: "新北区" },
  ];

  const valveStatuses = [
    { value: 1, label: "待普查" },
    { value: 2, label: "普查中" },
    { value: 3, label: "已普查" },
    { value: 4, label: "异常挂起" },
    { value: 5, label: "已签证" },
    { value: 6, label: "已结算" },
    { value: 7, label: "已挂接" },
  ];

  const wellCoverStatuses = [
    { value: 0, label: "破损" },
    { value: 1, label: "完好" },
  ];
  const jgczs = [
    { value: "复合", label: "复合" },
    { value: "铸铁", label: "铸铁" },
    { value: "水泥", label: "水泥" },

  ];

  const wellCoverLocations = [
    { value: 1, label: "机动车道" },
    { value: 2, label: "非机动车道" },
    { value: 3, label: "人行横道" },
    { value: 4, label: "人行道" },
    { value: 5, label: "绿化带" },
    { value: 6, label: "斑马线" },
    { value: 7, label: "泥土路面" },
    { value: 8, label: "铺装道板" }
  ];

  const wellCoverAbnormalDesc = [
    { value: 1, label: "正常" },
    { value: 2, label: "GIS有，现场灭失" },
    { value: 3, label: "GIS有，现场错位" },
    { value: 4, label: "GIS无，现场有" },
  ];

  const checkTypes = [
    { value: 0, label: "开井查验" },
    { value: 1, label: "不开井查验" },
  ];

  useEffect(() => {
    if (id && !isNaN(Number(id))) {
      fetchValveDetail();
    }
  }, [id]);

  useEffect(() => {
    // Set the initial status value when form is initialized
    const currentStatus = form.getFieldValue("status");
    if (currentStatus) {
      setPreviousStatus(currentStatus);
      setIsStatusProcessing(currentStatus === 2);
    }
  }, [valveData]);

  const handleStatusChange = (value) => {
    if (value === 2) {
      // If trying to change to "普查中"
      Modal.warning({
        title: "操作提示",
        content: "无法直接修改阀门至普查中，请前往使用派单功能",
        onOk: () => {
          // Reset to previous status
          form.setFieldsValue({ status: previousStatus });
        },
      });
    } else {
      // Update previous status
      setPreviousStatus(value);
      setIsStatusProcessing(value === 2);
    }
  };

  const fetchValveDetail = async () => {
    setLoading(true);
    try {
      const response = await valveService.getValveDetail(id);

      if (response.code === 0) {
        const data = response.data;
        setValveData(data);

        // Initialize image state variables with data from API
        setLocationPic(data.location_pic || null);
        setGisPic(data.gis_pic || null);
        setJgPicNear(data.jg_pic_near || null);
        setJgPicFar(data.jg_pic_far || null);

        form.setFieldsValue({
          ...data,
          pc_date: data.pc_date ? dayjs(data.pc_date) : null,
        });
      } else {
        message.error(response.data.message || "获取阀门详情失败");
      }
    } catch (error) {
      message.error("获取阀门详情失败：" + error.response?.data?.message ||  error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    // Prevent form submission if valve is in processing status
    if (isStatusProcessing) {
      message.error("阀门处于普查中状态，无法修改");
      return;
    }

    // Prevent form submission if any image is uploading
    if (isAnyImageUploading()) {
      message.error("图片上传中，请等待上传完成后再保存");
      return;
    }

    setLoading(true);
    try {
      // 创建基础提交数据
      const submitData = {
        ...values,
        // Image fields
        location_pic: location_pic,
        gis_pic: gis_pic,
        jg_pic_near: jg_pic_near,
        jg_pic_far: jg_pic_far,
        pc_date: values.pc_date?.format("YYYY-MM-DD HH:mm:ss"),
      };

      // 根据是创建还是更新状态，对jgzt和jgzb字段进行不同处理
      if (id !== "create") {
        // 更新状态：如果字段未填写，需要明确设置为null
        submitData.id = id;
        submitData.jgzt = values.jgzt === undefined ? null : values.jgzt;
        submitData.jgzb = values.jgzb === undefined ? null : values.jgzb;
      } else {
        // 创建状态：如果字段未填写，不传入相关参数
        if (values.jgzt !== undefined) {
          submitData.jgzt = values.jgzt;
        }
        if (values.jgzb !== undefined) {
          submitData.jgzb = values.jgzb;
        }
      }

      const response = await (id === "create"
        ? valveService.createValve(submitData)
        : valveService.updateValve(submitData));

      if (response.code === 0) {
        message.success("保存成功");
        // 记录当前页面，用于返回列表页时识别
        sessionStorage.setItem(
          "previousPage",
          JSON.stringify({
            url: window.location.pathname + window.location.search,
            timestamp: new Date().getTime(),
          })
        );
        navigate("/admin/valves");
      } else {
        message.error(response.message || "保存失败");
      }
    } catch (error) {
      message.error("保存失败：" + error.response?.data?.message ||  error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: "16px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h2 style={{ margin: 0 }}>阀门详情</h2>
        <Space>
          <Button
            type="primary"
            onClick={() => form.submit()}
            disabled={isStatusProcessing || isAnyImageUploading()}
          >
            {id === "create" ? "创建" : "保存"}
          </Button>
          <Button
            onClick={() => {
              // 记录当前页面，用于返回列表页时识别
              sessionStorage.setItem(
                "previousPage",
                JSON.stringify({
                  url: window.location.pathname + window.location.search,
                  timestamp: new Date().getTime(),
                })
              );
              navigate("/admin/valves");
            }}
          >
            返回
          </Button>
        </Space>
      </div>

      <Form
        form={form}
        layout="inline"
        onFinish={handleSubmit}
        initialValues={{
          status: 1,
        }}
        labelAlign="left"
        labelCol={{ style: { width: 80, flexShrink: 0 } }}
        disabled={isStatusProcessing && id !== "create"}
      >
        <Row gutter={[16, 16]} wrap={true}>
          <Col span={12}>
            <Space direction="vertical" size={16}>
              {/* 阀门系统信息 */}

              <Card style={{ backgroundColor: "#f6f6f6" }}>
                <Row gutter={[0, 14]}>
                  <Col span={24}>
                    <Form.Item name="id" label="阀门ID">
                      <Input
                        disabled
                        placeholder={id === "create" ? "创建后自动分配ID" : ""}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item name="fmlsh" label="GIS流水号">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="status"
                      label="阀门状态"
                      rules={[{ required: true, message: "请选择状态" }]}
                    >
                      <Select
                        options={valveStatuses}
                        onChange={handleStatusChange}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item name="pc_date" label="普查日期">
                      <DatePicker showTime style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
              {/* 阀门位置信息 */}
              <Card style={{ backgroundColor: "#f6f6f6" }}>
                <Row gutter={[0, 14]}>
                  <Col span={24}>
                    <Form.Item name="district" label="所属区域">
                      <Radio.Group
                        options={districtOptions}
                        placeholder="请选择所属区域"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item name="address" label="所在地点">
                      <Input showCount maxLength={80} />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <div className="fullUploaderLine">
                      <UploadImage
                        title="GIS位置图"
                        src={valveData.location_pic ?? null}
                        onSuccess={(result) => {
                          setLocationPic(result.url ?? null);
                          setUploadingImages((prev) => ({
                            ...prev,
                            location_pic: false,
                          }));
                        }}
                        onUploadStart={() => {
                          setUploadingImages((prev) => ({
                            ...prev,
                            location_pic: true,
                          }));
                        }}
                      ></UploadImage>
                      <UploadImage
                        title="GIS平面图"
                        src={valveData.gis_pic ?? null}
                        onSuccess={(result) => {
                          setGisPic(result.url ?? null);
                          setUploadingImages((prev) => ({
                            ...prev,
                            gis_pic: false,
                          }));
                        }}
                        onUploadStart={() => {
                          setUploadingImages((prev) => ({
                            ...prev,
                            gis_pic: true,
                          }));
                        }}
                      ></UploadImage>
                    </div>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="geometry_x"
                      label="X坐标"
                      rules={[
                        {
                          required: true,
                          message: "请输入X坐标",
                        },
                        {
                          pattern: /^[0-9.]*$/,
                          message: "只能输入数字和小数点",
                        },
                      ]}
                    >
                      <Input placeholder="请输入X坐标" />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="geometry_y"
                      label="Y坐标"
                      rules={[
                        {
                          required: true,
                          message: "请输入Y坐标",
                        },
                        {
                          pattern: /^[0-9.]*$/,
                          message: "只能输入数字和小数点",
                        },
                      ]}
                    >
                      <Input placeholder="请输入Y坐标" />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="geometry_z"
                      label="Z坐标"
                      rules={[
                        {
                          pattern: /^[0-9.]*$/,
                          message: "只能输入数字和小数点",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* 阀门信息 */}
              <Card style={{ backgroundColor: "#f6f6f6" }}>
                <Row gutter={[0, 14]}>
                  <Col span={24}>
                    <Form.Item name="subtype" label="阀门类型">
                      <Radio.Group options={valveTypes} />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item name="category" label="阀门类别">
                      <Select options={valveCategories} />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item
                      name="standard"
                      label="阀门口径"
                      rules={[
                        {
                          type: "number",
                          message: "阀门口径只能为数字",
                          transform: (value) => {
                            if (value === undefined || value === null || value === '') {
                              return undefined;
                            }
                            return Number(value);
                          },
                        },
                        {
                          type: "number",
                          max: 99999999,
                          message: "阀门口径不能超过8位数",
                        },
                        {
                          type: "number",
                          min: 0,
                          message: "阀门口径不能小于0",
                        },
                      ]}
                    >
                      <InputNumber prefix="DN" min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item name="depth" label="埋深">
                      <InputNumber
                        min={0}
                        suffix="米"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Space>
          </Col>

          <Col span={12}>
            <Space direction="vertical" size={16}>
              {/* 井盖信息 */}
              <Card style={{ backgroundColor: "#f6f6f6" }}>
                <Row gutter={[0, 14]}>
                <Col span={24}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Form.Item
                        name="jgycms"
                        label="井盖描述"
                        labelCol={{ style: { flexShrink: 0 } }}
                        style={{ flex: 1, marginBottom: 0 }}
                      >
                        <Radio.Group options={wellCoverAbnormalDesc} />
                      </Form.Item>
                    </div>
                  </Col>
                  <Col span={24}>
                    <Form.Item name="jgbh" label="井盖编号" rules={[{
                      pattern: /^[A-Za-z0-9]*$/,
                      message: "只能输入英文字母和数字"
                    }]}>
                      <Input showCount maxLength={80} />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item name="jgcj" label="井盖厂家">
                      <Input showCount maxLength={80} />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item name="jgcc" label="井盖尺寸">
                      <Input showCount maxLength={80} />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item name="jgcz" label="井盖材质">
                      <Radio.Group options={jgczs} />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Form.Item name="jgzt" label="井盖状态" style={{ flex: 1, marginBottom: 0 }}>
                        <Radio.Group options={wellCoverStatuses} />
                      </Form.Item>
                      <Button 
                        type="link" 
                        size="small"
                        onClick={() => {
                          form.resetFields(['jgzt']);
                        }}
                      >
                        重置
                      </Button>
                    </div>
                  </Col>
                  <Col span={24}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Form.Item name="jgzb" label="井盖周边" style={{ flex: 1, marginBottom: 0 }}>
                        <Radio.Group options={wellCoverStatuses} />
                      </Form.Item>
                      <Button 
                        type="link" 
                        size="small"
                        onClick={() => {
                          form.resetFields(['jgzb']);
                        }}
                      >
                        重置
                      </Button>
                    </div>
                  </Col>
                  <Col span={24}>
                    <Form.Item name="jgwz" label="井盖位置">
                      <Radio.Group options={wellCoverLocations} />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Form.Item name="cylb" label="查验类别" style={{ flex: 1, marginBottom: 0 }}>
                        <Radio.Group options={checkTypes} />
                      </Form.Item>
                      
                    </div>
                  </Col>
                  
                </Row>
              </Card>
              {/* 图片信息 */}
              <Card style={{ backgroundColor: "#f6f6f6" }}>
                <div className="fullUploaderLine">
                  <UploadImage
                    title="井内近景照"
                    src={valveData.jg_pic_near ?? null}
                    onSuccess={(result) => {
                      setJgPicNear(result.url ?? null);
                      setUploadingImages((prev) => ({
                        ...prev,
                        jg_pic_near: false,
                      }));
                    }}
                    onUploadStart={() => {
                      setUploadingImages((prev) => ({
                        ...prev,
                        jg_pic_near: true,
                      }));
                    }}
                  ></UploadImage>
                  <UploadImage
                    title="井内远景照"
                    src={valveData.jg_pic_far ?? null}
                    onSuccess={(result) => {
                      setJgPicFar(result.url ?? null);
                      setUploadingImages((prev) => ({
                        ...prev,
                        jg_pic_far: false,
                      }));
                    }}
                    onUploadStart={() => {
                      setUploadingImages((prev) => ({
                        ...prev,
                        jg_pic_far: true,
                      }));
                    }}
                  ></UploadImage>
                </div>
              </Card>
              <Card style={{ backgroundColor: "#f6f6f6" }}>
                <Col span={24}>
                  <Form.Item
                    name="memo"
                    label="备注"
                    layout="vertical"
                    labelCol={{ span: 24 }}
                  >
                    <Input.TextArea rows={4} showCount maxLength={500} />
                  </Form.Item>
                </Col>
              </Card>
            </Space>
          </Col>
        </Row>
      </Form>
    </div>
  );
}

export default ValvesDetail;

const valveTypes = [
  { value: 2, label: "蝶阀" },
  { value: 1, label: "闸阀" },
];

const valveCategories = [
  { value: 1, label: "小区进水阀门" },
  { value: 2, label: "小区环线阀门" },
  { value: 3, label: "小区楼幢阀门" },
  { value: 4, label: "小区单元阀门" },
  { value: 5, label: "小区消火栓阀门" },
  { value: 7, label: "市政管线阀门" },
  { value: 8, label: "市政管线消防栓阀门" },
  { value: 9, label: "小区泵房进水阀"},
  { value: 6, label: "小区其他" }
];
